import React, { useEffect, useState, useMemo, useRef, useContext } from 'react'
import VoiceTalentList from './components/VoiceTalentList'
import { Full as Loading } from 'components/Loading'
import VoiceTalent from './components/VoiceTalent'
import messages from "./messages"
import { FormattedMessage, useIntl } from 'react-intl'
import Select from 'react-select'
import useCurrentUser from 'hooks/useCurrentUser'
import Icon from 'components/Icon'
import { Button } from '@arnsbogroup/react-voicearchive'
import { BrandContext } from 'CustomerApp'

const CustomerRosterPage = () => {
  const { brand } = useContext(BrandContext)
  const { formatMessage } = useIntl()
  const [roster, setRoster] = useState({})
  const [selectedRoster, setSelectedRoster] = useState(null)
  const [filters, setFilters] = useState({})
  const [favoriteIds, setFavoriteIds] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const user = useCurrentUser()

  const uriRef = useRef(null)
  const [copied, setCopied] = useState(false)
  const [sharingLink, setSharingLink] = useState(false)

  const onCopyToClipBoard = () => {
    uriRef.current.select()
    document.execCommand('copy')
    setCopied(true)
  }

  useEffect(() => {
    fetch(`/rest/v2/customer/roster`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ data }) => {
        setRoster(data)
        setSelectedRoster(Object.keys(data)[0])
      })

    fetch(`/rest/v2/user/me/favorites`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then(({ data }) => {
        setFavoriteIds(data.supplier || [])
      })
  }, [])

    const onShare = () => {
      let payload = {
        initatedBy: "customer",
        voices: Object.fromEntries(
          roster[selectedRoster]
            .filter(({ id }) => selectedIds.includes(parseInt(id)))
            .map(({ userid, id }) => [userid, { id: parseInt(id) }])
        )
        ,
        collections: [],
        voiceages: [
            100,
            18
        ],
        includeAll: false,
        view: "GRID",
        details: {
          name: user?.name,
          email: user?.email
        },
        resolveBrand: false,
        createLead: false
      }

      if (brand?.uid) {
        payload.brand = {
          uid: brand.uid
        }
      }

      fetch(`/rest/v2/project`, {
        credentials: 'same-origin',
        method: 'POST',
        body: JSON.stringify(payload),
      })
      .then((response) => response.json())
      .then(({ success, data, errors, meta }) => {
        if (success) {
          setSharingLink(`${meta?.href?.anonymous}/vote`)
        } else {
          toastr.error(errors.join('. '))
        }
      })
    }

  const onFavorite = (voiceTalentId) => fetch(`/rest/v2/user/me/favorites`, {
    credentials: 'same-origin',
    method: favoriteIds.includes(parseInt(voiceTalentId, 10)) ? 'DELETE' : 'POST',
    body: JSON.stringify({ type: 'supplier', id: voiceTalentId }),
  })
    .then((response) => response.json())
    .then(({ data }) => {
      setFavoriteIds( data.supplier || [])
    })

  useEffect(() => {
    if (!selectedRoster) {
      return
    }

    setFilters({
      description: {
        name: formatMessage(messages.description),
        options: [...new Set(Object.values(roster[selectedRoster]).map(({ description }) => description))]
        .filter((value) => value !== '')
        .map((value) => ({ label: value, value })),
        value: null,
      },
      gender: {
        name: formatMessage(messages.gender),
        options: [...new Set(Object.values(roster[selectedRoster]).map(({ gender }) => gender))]
        .filter((value) => value !== '')
        .map((value) => ({ label: value, value })),
        value: null,
      },
      languages: {
        name: formatMessage(messages.languages),
        options: [...new Set(Object.values(roster[selectedRoster]).flatMap(entry => entry.languages.map(language => language.skillSet == 'native' ? language.name : null)))]
        .map((value) => ({label: value, value: value})).filter(({value}) => value),
        value: null,
      },
      dialects: {
        name: formatMessage(messages.dialects),
        options: formatDialectList(),
        value: null,
      },
      age: {
        name: formatMessage(messages.voiceAge),
        options: [...new Set(Object.values(roster[selectedRoster]).map(({ age }) => age))]
        .filter((value) => value !== '')
        .map((value) => ({ label: value, value })),
        value: null,
      },
    })
  }, [selectedRoster, roster])

  const formatDialectList = (languages = null) => {
    let allDialects = [];
    (Object.values(roster[selectedRoster]).map(
      ({ demos }) => {
        if (demos) {
          demos.forEach(demo => {
            if (languages) {
                languages.forEach(language => {
                  if (language.value === demo.language) {
                    allDialects.push({
                      'label': demo.dialect,
                      'value': demo.dialect,
                    });
                  }
                })
            } else if (demo.dialect) {
              allDialects.push({
                'label': demo.dialect,
                'value': demo.dialect,
              });
            }
          });
        }
      }
    ));

    let uniqueDialects = Object.values(
      allDialects.reduce((map, dialect) => ({ ...map, [dialect.value]: dialect }), {})
    )
    
    return uniqueDialects;
  }

  const list = useMemo(() => {
    if (roster && selectedRoster) {
      return roster[selectedRoster].filter((talent) => {
        return Object.entries(filters)
          .filter(([_, { value }]) => value && !!value.length)
          .every(([key, { value }]) => {
            if (key === 'dialects') {
              const talentHasDialect =  value.some(({ value }) => Object.values(talent['demos'] ? talent['demos'].map((demo) => demo['dialect'] ? demo['dialect'] : '') : []).includes(value))
              if (talentHasDialect) {
                const demoIndex = talent['demos'].findIndex(({ dialect }) => {
                  return value.find(o => o.value === dialect);
                });
                const correctDemo = talent['demos'][demoIndex];
                talent['demos'].splice(demoIndex, 1)
                talent['demos'].unshift(correctDemo)
              }
              return talentHasDialect;
            }
            if (key === 'languages') {
              const talentHasLanguage =  value.some(({ value }) => Object.values(talent['languages'] ? talent['languages'].map((language) => language['skillSet'] == 'native' ? language['name'] : '') : []).includes(value))
              return talentHasLanguage
            }
            if (typeof talent[key] === 'string') {
              return value.map(({ value }) => value).includes(talent[key])
            }
            return value.some(({ value }) => Object.values(talent[key]).includes(value))
          })
      })
    }

    return []
  }, [roster, selectedRoster, filters, favoriteIds, selectedIds])

  return (
    <div className="py-3 px-4 lg:py-5 lg:px-6 space-y-6">
      <div className="pb-5 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900 space-x-2">
          <select className="text-lg leading-6 font-medium text-primary" onChange={({ target: { value } }) => setSelectedRoster(value)}>
            { Object.entries(roster).map(([id, _]) => (
              <option key={`options-${id}`} value={id}>{ id }</option>
            )) }
          </select>

          <span>
            <FormattedMessage {...messages.title} />
          </span>
        </h3>
        <p className="mt-2 max-w-4xl text-sm text-gray-500">
          <FormattedMessage {...messages.subtitle} />
        </p>
      </div>

      { selectedIds.length > 0 &&
        <div className="p-2 rounded bg-primary shadow-lg sm:p-3">
          <div className="flex items-center justify-between flex-wrap">
            <div className="w-0 flex-1 flex items-center">
              <div className="flex p-2 rounded-lg bg-primary-darker items-center justify-center">
                <Icon icon="external-link"className="text-white" aria-hidden="true" />
              </div>
              <p className="ml-3 font-medium text-white truncate">
                <span className="md:hidden">{ selectedIds.length } voices selected. Click to share.</span>
                <span className="hidden md:inline">Share { selectedIds.length } selected voices, with a colleague or client by generating a unique link. Share on e-mail, chat or anywhere you want.</span>
              </p>
            </div>
            <div className="mt-2 shrink-0 w-full sm:mt-0 sm:w-auto">
              { sharingLink ? (
                <div className="text-sm grid grid-cols-6 p-2 bg-white rounded">
                  <div className="cursor-pointer col-span-5">
                    <input type="uri" value={sharingLink} className="w-full h-auto bg-transparent text-gray-700 px-2" ref={uriRef} onClick={() => uriRef.current.select()} readOnly />
                  </div>
                  <div className="text-right cursor-pointer hover:text-primary">
                    <Icon icon={copied ? 'check' : 'share'} onClick={onCopyToClipBoard} />
                  </div>
                </div>
              ) : (
                <Button
                  onClick={onShare}
                  variant="plain"
                  size="sm"
                >
                  Generate link
                </Button>
              ) }
            </div>
            <div className="order-2 shrink-0 sm:order-3 sm:ml-2">
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setCopied(false)
                  setSelectedIds([])
                  setSharingLink(null)
                }}
                icon={['fas', 'times']}
              />
            </div>
          </div>
        </div>
      }

      <div className="grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
        { Object.entries(filters).map(([key, { name, options, value }]) => (
          <Select
            key={key}
            isClearable
            placeholder={name}
            isMulti
            className="w-full text-sm text-gray-600"
            value={value}
            onChange={(values) => setFilters(key === 'languages' ? { ...filters, [key]: { ...filters[key], value: values }, dialects: { ...filters.dialects, options: formatDialectList(values) } } : { ...filters, [key]: { ...filters[key], value: values }})}
            openMenuOnFocus
            options={options}
          />
        )) }
      </div>

      <div>
        { !Object.keys(roster).length ? (
          <Loading />
        ) : (
          <ul className="mt-3 grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
            { list
                .sort((a, b) => favoriteIds.includes(parseInt(a.id, 10)) ? -1 : 1)
                .map((talent) => (
                  <VoiceTalent
                    key={talent.uid}
                    favorite={favoriteIds.includes(parseInt(talent.id, 10))}
                    onFavorite={() => onFavorite(talent.id)}
                    selected={selectedIds.includes(parseInt(talent.id, 10))}
                    onSelect={() => setSelectedIds(
                      selectedIds.includes(parseInt(talent.id, 10)) ?
                      selectedIds.filter((id) => id !== parseInt(talent.id, 10)) :
                      [ ...selectedIds, parseInt(talent.id, 10) ]
                    )}
                    {...talent}
                  />
            )) }
          </ul>
        ) }
      </div>
    </div>
  )
}

export default CustomerRosterPage
